import React, { useState } from 'react';
import { EventType } from '../../misc/types';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import DefaultButton from '../default-button/DefaultButton';
import Copy from '../../assets/icons/Copy';
import { getPsychologistAppointmentMeta } from '../../misc/utils';

type GridCell =
  | null
  | 'skip'
  | {
    event: EventType;
    rowSpan: number;
  };

interface WeeklyCalendarTableProps {
  days: Date[];
  timeSlots: string[];
  grid: GridCell[][];
  onEmptyCellClick: (
    day: Date,
    start: string,
    end: string,
    tdElement: HTMLTableCellElement
  ) => void;
  onEventClick: (event: EventType, tdElement: HTMLTableCellElement) => void;
  onCopyWeekClick: (tdElement: HTMLButtonElement, day: Date) => void;
  popupVisibleIndex: number | null;
  setPopupVisibleIndex: (value: number | null) => void;
}

const WeeklyCalendarTable: React.FC<WeeklyCalendarTableProps> = ({
  days,
  timeSlots,
  grid,
  onEmptyCellClick,
  onEventClick,
  onCopyWeekClick,
  popupVisibleIndex,
  setPopupVisibleIndex,
}) => {
  const [popupPosition, setPopupPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const handlePopupToggle = (day: Date, index: number, tdElement: HTMLButtonElement) => {
    if (popupVisibleIndex === index) {
      setPopupVisibleIndex(null);
      setPopupPosition(null);
    } else {
      const rect = tdElement.getBoundingClientRect();
      setPopupVisibleIndex(index);
      setPopupPosition({
        top: rect.top + window.scrollY + tdElement.offsetHeight,
        left: rect.left + window.scrollX,
      });

      onCopyWeekClick(tdElement, day);
    }
  };

  return (
    <div className="overflow-auto">
      <table className="w-full border-collapse table-fixed">
        <thead>
          <tr>
            <th className="w-12 border border-gray-200" />
            {days.map((day, dIndex) => {
              const hasEvents = grid.map(r => r[dIndex]).some(i => Boolean(i) && i !== 'skip')
              return (
                <th
                  key={dIndex}
                  className="border border-gray-200 bg-gray-100 py-4 w-[125px]"
                >
                  <div className="flex items-center px-2 justify-center gap-2">
                    <span className="text-gray-700 font-medium">
                      {format(day, 'EEE, dd', { locale: ru })}
                    </span>
                    {hasEvents && (
                      <DefaultButton
                        Icon={Copy}
                        iconStyle="w-4 h-4"
                        additionalStyle={`py-1 px-1 rounded ${popupVisibleIndex === dIndex
                          ? 'bg-blue-500 text-white'
                          : 'bg-transparent text-gray-500 border border-gray-300'
                          }`}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
                          handlePopupToggle(day, dIndex, e.currentTarget)
                        }
                      />
                    )}
                  </div>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {timeSlots.map((slot, rowIndex) => (
            <tr
              key={rowIndex}
              className={`${slot.endsWith(':30')
                ? 'border-none '
                : 'border-t border-gray-200'
                }`}
              style={{ height: '40px' }}
            >
              {/* Time label on left */}
              <td
                className="relative text-center border-r border-gray-200"
                style={{ position: 'relative' }}
              >
                {slot.endsWith(':00') && (
                  <div
                    className="absolute left-1/2 transform -translate-x-1/2"
                    style={{
                      top: '-10px',
                      background: 'white',
                    }}
                  >
                    {slot}
                  </div>
                )}
              </td>

              {/* Days columns */}
              {days.map((day, colIndex) => {
                const cell = grid[rowIndex][colIndex];
                if (cell === 'skip') {
                  return null; // skip cell
                }
                if (cell && cell.event) {
                  const { event, rowSpan } = cell;
                  const clientMeta = getPsychologistAppointmentMeta(event)
                  return (
                    <td
                      key={colIndex}
                      rowSpan={rowSpan}
                      className="relative text-center border-r border-gray-200 p-0 cursor-pointer calendar-cell"
                      onClick={(e) => onEventClick(event, e.currentTarget)}
                    >
                      <div
                        className={`m-0.5 absolute inset-0 rounded-lg text-center align-middle flex flex-col gap-1 justify-center items-center ${event.canceled ? 'text-red-600' : 'text-[#5F6B88]'
                          }`}
                        style={{
                          backgroundColor: clientMeta?.bgColor
                        }}
                      >
                        <div
                          className={`text-[12px] font-medium rounded leading-[14.4px] ${event.title === 'Свободное время'
                            ? 'text-[#5F6B88]'
                            : 'bg-blue-500 text-white px-1 py-0.5'
                            }`}
                        >
                          {event.title}
                        </div>
                        <div className="text-xs text-[#5F6B88]">
                          {event.start} - {event.end}
                        </div>
                        {Boolean(clientMeta.text) && (
                          <div
                            className='rounded-lg px-1 text-[12px]'
                            style={{
                              backgroundColor: clientMeta.textBg,
                              color: clientMeta.textColor
                            }}
                          >
                            {clientMeta.text}
                          </div>
                        )}
                      </div>
                    </td>
                  );
                }

                let end = '';
                const nextIndex = rowIndex + 1;
                if (nextIndex < timeSlots.length) {
                  end = timeSlots[nextIndex + 1];
                }
                return (
                  <td
                    key={colIndex}
                    className="relative border-r border-gray-200 p-0 cursor-pointer calendar-cell"
                    onClick={(e) => {
                      if (!end) return;
                      onEmptyCellClick(day, slot, end, e.currentTarget);
                    }}
                  ></td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default WeeklyCalendarTable;
